<template>
  <transition name="oad-slide">
    <div v-if="open" class="oad-overlay" @click.self="close">
      <div class="oad-drawer" role="dialog" aria-modal="true">
        <header class="oad-header">
          <div class="oad-header-info">
            <span class="oad-eyebrow">DETALHE DO OPERADOR</span>
            <h3 class="oad-title">{{ operatorName }}</h3>
          </div>
          <button type="button" class="oad-close" @click="close" aria-label="Fechar">
            <i class="bx bx-x"></i>
          </button>
        </header>

        <div v-if="isLoading && !hasContent" class="oad-loading">
          <div class="oad-skel oad-skel-kpi"></div>
          <div class="oad-skel oad-skel-list"></div>
        </div>

        <div v-else class="oad-body">
          <!-- KPIs -->
          <div class="oad-kpi-grid">
            <div class="oad-kpi">
              <div class="oad-kpi-label">Pessoas atendidas</div>
              <div class="oad-kpi-value oad-kpi-value-primary">{{ kpis.total_unique || 0 }}</div>
              <div class="oad-kpi-sub">{{ kpis.total_reads || 0 }} leituras totais</div>
            </div>
            <div class="oad-kpi">
              <div class="oad-kpi-label">Velocidade</div>
              <div class="oad-kpi-value">
                {{ kpis.reads_per_min !== null && kpis.reads_per_min !== undefined ? kpis.reads_per_min : '—' }}
                <span class="oad-kpi-suffix">/min</span>
              </div>
              <div class="oad-kpi-sub">média no período</div>
            </div>
            <div class="oad-kpi">
              <div class="oad-kpi-label">Distribuição</div>
              <div class="oad-kpi-value oad-kpi-value-small">
                {{ kpis.ins || 0 }} <span class="oad-kpi-suffix">entradas</span>
              </div>
              <div class="oad-kpi-sub">{{ kpis.outs || 0 }} saídas</div>
            </div>
            <div class="oad-kpi">
              <div class="oad-kpi-label">Janela analisada</div>
              <div class="oad-kpi-value oad-kpi-value-small">
                <span>{{ formatShortDate(kpis.first_read_at) }}</span>
                <span class="oad-kpi-sep">→</span>
                <span>{{ formatShortDate(kpis.last_read_at) }}</span>
              </div>
              <div class="oad-kpi-sub">{{ formatRelativeWindow() }}</div>
            </div>
          </div>

          <!-- Histograma de quando atendeu -->
          <section
            v-if="histogram && histogram.peak_count > 0"
            class="oad-section"
          >
            <header class="oad-section-head">
              <h4 class="oad-section-title">Quando atendeu</h4>
              <span v-if="peakLabel" class="oad-section-hint">pico em {{ peakLabel }}</span>
            </header>
            <div class="oad-histogram">
              <div class="oad-histogram-bars">
                <span
                  v-for="b in histogram.buckets"
                  :key="b.idx"
                  class="oad-histogram-bar"
                  :class="{ 'oad-histogram-bar-peak': b.idx === histogram.peak_idx }"
                  :style="{ height: barHeight(b.count, histogram.peak_count) + '%' }"
                  :title="`${b.label} · ${b.count} leituras`"
                ></span>
              </div>
              <div class="oad-histogram-axis">
                <span>{{ histogram.buckets[0].label }}</span>
                <span>{{ histogram.buckets[histogram.buckets.length - 1].label }}</span>
              </div>
            </div>
          </section>

          <!-- Top tags atendidas -->
          <section v-if="topTags && topTags.length > 0" class="oad-section">
            <header class="oad-section-head">
              <h4 class="oad-section-title">Locais atendidos</h4>
              <span class="oad-section-hint">onde mais bipou</span>
            </header>
            <div class="oad-tag-list">
              <div
                v-for="(t, idx) in topTags"
                :key="t.tag_id"
                class="oad-tag-row"
              >
                <span class="oad-tag-rank">{{ idx + 1 }}</span>
                <span class="oad-tag-name" :title="t.tag_name">{{ t.tag_name }}</span>
                <div class="oad-tag-bar">
                  <div
                    class="oad-tag-fill"
                    :style="{ width: tagBarPct(t.count) + '%' }"
                  ></div>
                </div>
                <span class="oad-tag-count">{{ t.count }}</span>
              </div>
            </div>
          </section>

          <!-- Perfil dos atendidos por campo — feature principal -->
          <section class="oad-section">
            <header class="oad-section-head">
              <h4 class="oad-section-title">Perfil dos atendidos</h4>
              <div class="oad-section-actions">
                <span v-if="fieldSelected" class="oad-field-chip">{{ fieldSelectedLabel }}</span>
                <span v-if="fieldSecondary" class="oad-field-chip-cross">
                  <i class="bx bx-x"></i>
                </span>
                <span v-if="fieldSecondary" class="oad-field-chip oad-field-chip-secondary">
                  {{ fieldSecondaryLabel }}
                </span>
                <button
                  v-if="fieldSelected"
                  class="oad-field-change"
                  @click="openPrimaryPicker"
                  title="Trocar campo principal"
                >
                  <i class="bx bx-transfer-alt"></i>
                </button>
              </div>
            </header>

            <div v-if="!fieldSelected" class="oad-field-placeholder" @click="openPrimaryPicker">
              <i class="bx bx-stats"></i>
              <div class="oad-field-placeholder-text">Quem foi atendido por {{ operatorFirstName }}?</div>
              <div class="oad-field-placeholder-sub">
                Cargo, grupo, cidade — escolha um campo pra ver o perfil de
                quem passou pelas leituras dele.
              </div>
            </div>

            <div v-else-if="fieldLoading" class="oad-field-loading">
              <i class="bx bx-loader-alt oad-spin"></i>
              <span>Cruzando dados…</span>
            </div>

            <div v-else-if="fieldRows.length === 0" class="oad-field-empty">
              <i class="bx bx-data"></i>
              <p>Nenhum atendido com este campo preenchido</p>
            </div>

            <!-- Single mode -->
            <div v-else-if="!fieldSecondary" class="oad-field-ranking">
              <div class="oad-field-meta-row">
                <span class="oad-field-meta">
                  <i class="bx bx-group"></i>
                  {{ fieldData.total }} respostas entre os atendidos
                </span>
                <button
                  class="oad-field-cross-btn"
                  @click="openSecondaryPicker"
                  title="Cruzar com outro campo"
                >
                  <i class="bx bx-shuffle"></i>
                  Cruzar com…
                </button>
              </div>
              <div
                v-for="(row, i) in fieldRows.slice(0, 10)"
                :key="row.value + i"
                class="oad-field-row"
              >
                <div class="oad-field-num" :class="fieldRankClass(i)">{{ i + 1 }}</div>
                <div class="oad-field-body">
                  <div class="oad-field-label" :title="row.value">{{ row.value }}</div>
                  <div class="oad-field-bar">
                    <div
                      class="oad-field-fill"
                      :style="{ width: fieldBarWidth(row.count) }"
                    ></div>
                  </div>
                </div>
                <div class="oad-field-right">
                  <span class="oad-field-count">{{ row.count }}</span>
                  <span class="oad-field-pct">{{ fieldPct(row.count) }}%</span>
                </div>
              </div>
            </div>

            <!-- Cross mode -->
            <div v-else class="oad-field-cross-list">
              <div class="oad-field-meta-row">
                <span class="oad-field-meta">
                  <i class="bx bx-group"></i>
                  {{ fieldData.total }} em {{ fieldGroupedRows.length }}
                  {{ fieldGroupedRows.length === 1 ? 'grupo' : 'grupos' }}
                </span>
                <button
                  class="oad-field-cross-btn oad-field-cross-btn-active"
                  @click="onClearFieldSecondary"
                  title="Voltar para visão simples"
                >
                  <i class="bx bx-x"></i>
                  Remover cruzamento
                </button>
              </div>

              <div
                v-for="(group, gIdx) in fieldGroupedRows"
                :key="group.primary + gIdx"
                class="oad-field-cross-group"
              >
                <div class="oad-field-cross-head">
                  <span class="oad-field-num" :class="fieldRankClass(gIdx)">{{ gIdx + 1 }}</span>
                  <span class="oad-field-cross-name" :title="group.primary">{{ group.primary }}</span>
                  <span class="oad-field-cross-count">{{ group.total }}</span>
                  <span class="oad-field-cross-pct">{{ fieldGroupPct(group.total) }}%</span>
                </div>
                <div class="oad-field-cross-children">
                  <div
                    v-for="(child, cIdx) in group.items.slice(0, 5)"
                    :key="child.secondary_value + cIdx"
                    class="oad-field-cross-child"
                  >
                    <span class="oad-field-cross-child-label" :title="child.secondary_value">{{ child.secondary_value }}</span>
                    <div class="oad-field-cross-child-bar">
                      <div
                        class="oad-field-cross-child-fill"
                        :style="{ width: fieldBarWidthRel(child.count, group.max) }"
                      ></div>
                    </div>
                    <span class="oad-field-cross-child-count">{{ child.count }}</span>
                  </div>
                  <div v-if="group.items.length > 5" class="oad-field-cross-more">
                    + {{ group.items.length - 5 }} {{ group.items.length - 5 === 1 ? 'outro' : 'outros' }}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Leituras recentes -->
          <section v-if="recentReads && recentReads.length > 0" class="oad-section">
            <header class="oad-section-head">
              <h4 class="oad-section-title">Leituras recentes</h4>
              <span class="oad-section-hint">{{ recentReads.length }} mais novas</span>
            </header>
            <div class="oad-recent-list">
              <div
                v-for="r in recentReads"
                :key="r.id"
                class="oad-recent-row"
                :class="`oad-recent-${r.event_type === 'CheckedIn' ? 'in' : 'out'}`"
              >
                <span class="oad-recent-icon">
                  <i :class="r.event_type === 'CheckedIn' ? 'bx bx-log-in-circle' : 'bx bx-log-out-circle'"></i>
                </span>
                <div class="oad-recent-body">
                  <span class="oad-recent-name" :title="r.participant_name">{{ r.participant_name || 'Anônimo' }}</span>
                  <span v-if="r.tag_name" class="oad-recent-tag">@ {{ r.tag_name }}</span>
                </div>
                <span class="oad-recent-time">{{ formatTimeOnly(r.event_date) }}</span>
              </div>
            </div>
          </section>
        </div>

        <!-- Modal picker (primary/secondary controlado por pickerMode) -->
        <div v-if="showFieldPicker" class="oad-picker-overlay" @click.self="closeFieldPicker">
          <div class="oad-picker">
            <div class="oad-picker-header">
              <div class="oad-picker-header-left">
                <i class="bx bx-search-alt-2 oad-picker-header-icon"></i>
                <span class="oad-picker-title">{{ pickerTitle }}</span>
              </div>
              <button class="oad-picker-close" @click="closeFieldPicker">
                <i class="bx bx-x"></i>
              </button>
            </div>

            <div v-if="pickerMode === 'secondary' && fieldSelected" class="oad-picker-context">
              <span class="oad-picker-context-label">Cruzando com</span>
              <span class="oad-field-chip oad-picker-context-chip">{{ fieldSelectedLabel }}</span>
            </div>

            <div class="oad-picker-body">
              <div
                v-for="group in pickerFieldsByGroup"
                :key="group.name"
                class="oad-picker-group"
              >
                <div class="oad-picker-group-label">{{ group.name }}</div>
                <div class="oad-picker-fields">
                  <button
                    v-for="f in group.items"
                    :key="fieldKey(f)"
                    class="oad-picker-field"
                    :class="{ selected: isFieldSelectedInPicker(f) }"
                    @click="onPickField(f)"
                  >
                    <span>{{ f.label }}</span>
                    <i v-if="isFieldSelectedInPicker(f)" class="bx bx-check"></i>
                  </button>
                </div>
              </div>
            </div>

            <div v-if="hasSelectionInPicker" class="oad-picker-footer">
              <button class="oad-picker-clear" @click="onClearInPicker">
                <i class="bx bx-trash"></i>
                {{ pickerMode === 'secondary' ? 'Remover cruzamento' : 'Limpar seleção' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'operator-analytics-drawer',
  props: {
    open: { type: Boolean, default: false },
    detail: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
    availableFields: { type: Array, default: () => [] },
    fieldSelected: { type: Object, default: null },
    fieldSecondary: { type: Object, default: null },
    fieldData: { type: Object, default: null },
    fieldLoading: { type: Boolean, default: false }
  },
  data: () => ({
    showFieldPicker: false,
    pickerMode: 'primary' // 'primary' | 'secondary'
  }),
  watch: {
    open (val) {
      document.body.style.overflow = val ? 'hidden' : ''
      if (!val) this.showFieldPicker = false
    }
  },
  beforeDestroy () {
    document.body.style.overflow = ''
  },
  computed: {
    hasContent () {
      return this.detail && this.detail.kpis && Object.keys(this.detail.kpis).length > 0
    },
    operatorName () {
      return this.detail && this.detail.operator ? this.detail.operator.name : '—'
    },
    operatorFirstName () {
      const full = this.operatorName || ''
      return full.split(' ')[0] || full
    },
    kpis () {
      return (this.detail && this.detail.kpis) || {}
    },
    histogram () {
      return this.detail && this.detail.histogram
    },
    peakLabel () {
      if (!this.histogram || this.histogram.peak_idx === null) return null
      const peak = this.histogram.buckets[this.histogram.peak_idx]
      return peak ? peak.label : null
    },
    topTags () {
      return (this.detail && this.detail.top_tags) || []
    },
    maxTagCount () {
      return this.topTags[0] ? this.topTags[0].count || 1 : 1
    },
    recentReads () {
      return (this.detail && this.detail.recent_reads) || []
    },
    fieldsByGroup () {
      return this.groupFields(this.availableFields)
    },
    pickerFieldsByGroup () {
      if (this.pickerMode === 'secondary' && this.fieldSelected) {
        const filtered = this.availableFields.filter(
          f => !(f.source === this.fieldSelected.source &&
                 String(f.id) === String(this.fieldSelected.id))
        )
        return this.groupFields(filtered)
      }
      return this.fieldsByGroup
    },
    pickerTitle () {
      return this.pickerMode === 'secondary'
        ? 'Cruzar com qual campo?'
        : 'Escolher campo para análise'
    },
    hasSelectionInPicker () {
      return this.pickerMode === 'secondary' ? !!this.fieldSecondary : !!this.fieldSelected
    },
    fieldSelectedLabel () {
      if (!this.fieldSelected) return ''
      const f = this.availableFields.find(
        x => x.source === this.fieldSelected.source &&
        String(x.id) === String(this.fieldSelected.id)
      )
      return f ? f.label : ''
    },
    fieldSecondaryLabel () {
      if (!this.fieldSecondary) return ''
      const f = this.availableFields.find(
        x => x.source === this.fieldSecondary.source &&
        String(x.id) === String(this.fieldSecondary.id)
      )
      return f ? f.label : ''
    },
    fieldRows () {
      return (this.fieldData && this.fieldData.rows) || []
    },
    fieldChartMax () {
      return this.fieldRows[0] ? this.fieldRows[0].count || 1 : 1
    },
    fieldGroupedRows () {
      const map = new Map()
      const groups = []
      this.fieldRows.forEach(row => {
        const key = row.primary_value
        let g = map.get(key)
        if (!g) {
          g = { primary: key, items: [], total: 0, max: 0 }
          map.set(key, g)
          groups.push(g)
        }
        g.items.push(row)
        g.total += row.count
        if (row.count > g.max) g.max = row.count
      })
      groups.sort((a, b) => b.total - a.total)
      return groups.slice(0, 6)
    }
  },
  methods: {
    close () {
      this.$emit('close')
    },
    barHeight (count, peak) {
      if (!peak) return 6
      if (count === 0) return 6
      const pct = (count / peak) * 100
      return Math.max(15, Math.min(100, pct))
    },
    tagBarPct (count) {
      return Math.max(3, Math.min(100, Math.round((count / this.maxTagCount) * 100)))
    },
    formatShortDate (datetime) {
      if (!datetime) return '—'
      const d = new Date(datetime.replace ? datetime.replace(' ', 'T') : datetime)
      const day = String(d.getDate()).padStart(2, '0')
      const months = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
      const m = months[d.getMonth()]
      const time = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0')
      return `${day}/${m} ${time}`
    },
    formatTimeOnly (datetime) {
      if (!datetime) return ''
      const d = new Date(datetime.replace ? datetime.replace(' ', 'T') : datetime)
      return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0')
    },
    formatRelativeWindow () {
      const k = this.kpis
      if (!k.first_read_at || !k.last_read_at) return '—'
      const a = new Date(k.first_read_at.replace ? k.first_read_at.replace(' ', 'T') : k.first_read_at)
      const b = new Date(k.last_read_at.replace ? k.last_read_at.replace(' ', 'T') : k.last_read_at)
      const diffMin = Math.max(1, Math.round((b.getTime() - a.getTime()) / 60000))
      if (diffMin < 60) return `${diffMin} min de cobertura`
      const h = Math.floor(diffMin / 60)
      const m = diffMin - h * 60
      if (h < 24) return m === 0 ? `${h}h de cobertura` : `${h}h${m}min de cobertura`
      return `${Math.floor(h / 24)} dia(s) de cobertura`
    },
    fieldKey (f) {
      return f ? `${f.source}::${f.id}` : ''
    },
    groupFields (fields) {
      const groups = new Map()
      fields.forEach(f => {
        const g = f.group || 'Outros'
        if (!groups.has(g)) groups.set(g, { name: g, items: [] })
        groups.get(g).items.push(f)
      })
      return Array.from(groups.values())
    },
    isFieldSelectedInPicker (f) {
      const target = this.pickerMode === 'secondary' ? this.fieldSecondary : this.fieldSelected
      return target &&
        target.source === f.source &&
        String(target.id) === String(f.id)
    },
    openPrimaryPicker () {
      this.pickerMode = 'primary'
      this.showFieldPicker = true
    },
    openSecondaryPicker () {
      this.pickerMode = 'secondary'
      this.showFieldPicker = true
    },
    closeFieldPicker () {
      this.showFieldPicker = false
    },
    onPickField (f) {
      const ref = { source: f.source, id: f.id }
      if (this.pickerMode === 'secondary') {
        this.$emit('change-field-secondary', ref)
      } else {
        this.$emit('change-field', ref)
      }
      this.showFieldPicker = false
    },
    onClearInPicker () {
      if (this.pickerMode === 'secondary') {
        this.$emit('change-field-secondary', null)
      } else {
        this.$emit('change-field', null)
      }
      this.showFieldPicker = false
    },
    onClearFieldSecondary () {
      this.$emit('change-field-secondary', null)
    },
    fieldRankClass (i) {
      if (i === 0) return 'oad-field-num--gold'
      if (i === 1) return 'oad-field-num--silver'
      if (i === 2) return 'oad-field-num--bronze'
      return ''
    },
    fieldBarWidth (count) {
      const pct = Math.max(2, Math.round((count / this.fieldChartMax) * 100))
      return `${pct}%`
    },
    fieldBarWidthRel (count, max) {
      const pct = Math.max(2, Math.round((count / Math.max(1, max)) * 100))
      return `${pct}%`
    },
    fieldPct (count) {
      const total = this.fieldData && this.fieldData.total ? this.fieldData.total : 1
      return Math.round((count / total) * 100)
    },
    fieldGroupPct (groupTotal) {
      const total = this.fieldData && this.fieldData.total ? this.fieldData.total : 1
      return Math.round((groupTotal / total) * 100)
    }
  }
}
</script>

<style lang="scss" scoped>
/* Layout reaproveita o padrão visual do TagAnalyticsDrawer */
.oad-overlay {
  position: fixed;
  inset: 0;
  background: rgba(26, 29, 38, 0.4);
  z-index: 1050;
  display: flex;
  justify-content: flex-end;
}

.oad-slide-enter-active,
.oad-slide-leave-active {
  transition: opacity 0.3s ease;
  .oad-drawer { transition: transform 0.32s cubic-bezier(0.34, 1.2, 0.64, 1); }
}
.oad-slide-enter, .oad-slide-leave-to {
  opacity: 0;
  .oad-drawer { transform: translateX(40px); }
}

.oad-drawer {
  width: 560px;
  max-width: 100vw;
  height: 100vh;
  background: #fff;
  display: flex;
  flex-direction: column;
  box-shadow: -16px 0 40px rgba(26, 29, 38, 0.2);
  overflow: hidden;
  position: relative;
}

.oad-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 26px 16px;
  border-bottom: 1px solid #f1f3f5;
  background: #fafbfc;
  flex-shrink: 0;
}

.oad-eyebrow {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  color: #8b90a5;
  text-transform: uppercase;
}

.oad-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a1d26;
  margin: 4px 0 0 0;
}

.oad-close {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: none;
  background: #fff;
  color: #8b90a5;
  font-size: 22px;
  cursor: pointer;
  display: grid;
  place-items: center;
  flex-shrink: 0;

  &:hover {
    background: #eaecf2;
    color: #1a1d26;
  }
}

.oad-loading {
  padding: 24px 26px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.oad-skel {
  background: linear-gradient(90deg, #f0f1f6 0%, #f8f9fc 50%, #f0f1f6 100%);
  background-size: 200% 100%;
  border-radius: 8px;
  animation: oad-shimmer 1.6s ease-in-out infinite;
}
.oad-skel-kpi { height: 120px; }
.oad-skel-list { height: 200px; }

@keyframes oad-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.oad-body {
  flex: 1;
  overflow-y: auto;
  padding: 22px 26px;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: #d0d5dd; border-radius: 99px; }
}

.oad-kpi-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 22px;
}

.oad-kpi {
  background: #fafbfc;
  border: 1px solid #eef0f6;
  border-radius: 8px;
  padding: 12px 14px;
}

.oad-kpi-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8b90a5;
  margin-bottom: 6px;
}

.oad-kpi-value {
  font-size: 22px;
  font-weight: 800;
  color: #1a1d26;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
  line-height: 1.1;

  &.oad-kpi-value-primary { color: #556ee6; }

  &.oad-kpi-value-small {
    font-size: 13px;
    font-weight: 600;
    color: #4a5068;
  }
}

.oad-kpi-suffix {
  font-size: 11px;
  font-weight: 500;
  color: #8b90a5;
  margin-left: 2px;
}

.oad-kpi-sep {
  margin: 0 4px;
  color: #b0b5c8;
}

.oad-kpi-sub {
  font-size: 11px;
  color: #8b90a5;
  margin-top: 4px;
  font-weight: 500;
}

.oad-section {
  margin-bottom: 22px;

  &:last-child { margin-bottom: 0; }
}

.oad-section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.oad-section-title {
  font-size: 13px;
  font-weight: 700;
  color: #1a1d26;
  margin: 0;
}

.oad-section-hint {
  font-size: 11px;
  color: #8b90a5;
  font-weight: 500;
}

.oad-section-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Histograma */
.oad-histogram {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.oad-histogram-bars {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 56px;
}

.oad-histogram-bar {
  flex: 1;
  min-width: 5px;
  background: rgba(85, 110, 230, 0.22);
  border-radius: 3px 3px 0 0;

  &.oad-histogram-bar-peak {
    background: #556ee6;
  }
}

.oad-histogram-axis {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  font-weight: 600;
  color: #b0b5c8;
}

/* Tags atendidas */
.oad-tag-list {
  display: flex;
  flex-direction: column;
}

.oad-tag-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
}

.oad-tag-rank {
  width: 22px;
  height: 22px;
  border-radius: 5px;
  background: #f5f6fa;
  color: #8b90a5;
  font-size: 11px;
  font-weight: 700;
  display: grid;
  place-items: center;
  flex-shrink: 0;
}

.oad-tag-name {
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  color: #1a1d26;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.oad-tag-bar {
  width: 80px;
  height: 5px;
  background: #f1f3f5;
  border-radius: 99px;
  overflow: hidden;
  flex-shrink: 0;
}

.oad-tag-fill {
  height: 100%;
  background: #556ee6;
  border-radius: 99px;
}

.oad-tag-count {
  font-size: 12px;
  font-weight: 700;
  color: #1a1d26;
  font-variant-numeric: tabular-nums;
  min-width: 28px;
  text-align: right;
  flex-shrink: 0;
}

/* Perfil dos atendidos por campo */
.oad-field-chip {
  padding: 2px 10px;
  background: rgba(85, 110, 230, 0.08);
  border: 1px solid rgba(85, 110, 230, 0.2);
  border-radius: 5px;
  font-size: 11px;
  font-weight: 500;
  color: #556ee6;
}

.oad-field-change {
  width: 28px;
  height: 28px;
  border: 1px solid #eaecf2;
  border-radius: 6px;
  background: #fff;
  color: #8b90a5;
  cursor: pointer;
  display: grid;
  place-items: center;
  font-size: 14px;

  &:hover { color: #556ee6; border-color: #556ee6; }
}

.oad-field-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 16px;
  border: 2px dashed #d0d4e8;
  border-radius: 10px;
  cursor: pointer;
  text-align: center;

  &:hover {
    border-color: #556ee6;
    background: rgba(85, 110, 230, 0.03);
  }

  i {
    font-size: 28px;
    color: #556ee6;
    margin-bottom: 10px;
  }
}

.oad-field-placeholder-text {
  font-size: 13px;
  font-weight: 600;
  color: #4a5068;
  margin-bottom: 4px;
}

.oad-field-placeholder-sub {
  font-size: 12px;
  color: #8b90a5;
  max-width: 320px;
  line-height: 1.5;
}

.oad-field-loading,
.oad-field-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 28px;
  color: #8b90a5;
  font-size: 13px;

  p { margin: 0; font-size: 12px; }
  i { font-size: 18px; color: #b0b5c8; }
}

.oad-spin {
  color: #556ee6 !important;
  animation: oad-spin 0.9s linear infinite;
}

@keyframes oad-spin {
  to { transform: rotate(360deg); }
}

.oad-field-meta-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.oad-field-meta {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 500;
  background: #f5f6fa;
  color: #4a5068;

  i { color: #8b90a5; font-size: 13px; }
}

.oad-field-cross-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border: 1px dashed rgba(139, 92, 246, 0.4);
  border-radius: 5px;
  background: transparent;
  color: #8b5cf6;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  i { font-size: 13px; }

  &:hover {
    background: rgba(139, 92, 246, 0.06);
    border-style: solid;
  }

  &.oad-field-cross-btn-active {
    background: rgba(139, 92, 246, 0.08);
    border-style: solid;
  }
}

.oad-field-chip-cross {
  color: #b0b5c8;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
}

.oad-field-chip-secondary {
  background: rgba(139, 92, 246, 0.08);
  border: 1px solid rgba(139, 92, 246, 0.25);
  color: #8b5cf6;
}

.oad-field-ranking {
  display: flex;
  flex-direction: column;
}

.oad-field-cross-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.oad-field-cross-group {
  background: #fafbfc;
  border: 1px solid #eef0f6;
  border-radius: 8px;
  padding: 10px 12px;
}

.oad-field-cross-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #f0f1f6;
}

.oad-field-cross-name {
  flex: 1;
  font-size: 12px;
  font-weight: 700;
  color: #1a1d26;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.oad-field-cross-count {
  font-size: 13px;
  font-weight: 800;
  color: #1a1d26;
  font-variant-numeric: tabular-nums;
}

.oad-field-cross-pct {
  font-size: 10px;
  font-weight: 600;
  color: #8b5cf6;
  background: rgba(139, 92, 246, 0.08);
  padding: 1px 5px;
  border-radius: 4px;
}

.oad-field-cross-children {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.oad-field-cross-child {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
}

.oad-field-cross-child-label {
  flex: 0 0 38%;
  color: #4a5068;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.oad-field-cross-child-bar {
  flex: 1;
  height: 4px;
  background: #f0f1f6;
  border-radius: 99px;
  overflow: hidden;
}

.oad-field-cross-child-fill {
  height: 100%;
  background: linear-gradient(90deg, #8b5cf6, #a78bfa);
  border-radius: 99px;
}

.oad-field-cross-child-count {
  font-size: 11px;
  font-weight: 700;
  color: #1a1d26;
  font-variant-numeric: tabular-nums;
  min-width: 24px;
  text-align: right;
}

.oad-field-cross-more {
  font-size: 10px;
  color: #8b90a5;
  font-style: italic;
  padding: 2px 0 0;
}

.oad-picker-context {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 18px;
  background: rgba(85, 110, 230, 0.04);
  border-bottom: 1px solid #f1f3f5;
}

.oad-picker-context-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8b90a5;
}

.oad-picker-context-chip {
  margin: 0;
}

.oad-field-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
}

.oad-field-num {
  width: 22px;
  height: 22px;
  border-radius: 5px;
  font-size: 10px;
  font-weight: 800;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  background: #f5f6fa;
  color: #b0b5c8;

  &.oad-field-num--gold   { background: #fef3c7; color: #d97706; }
  &.oad-field-num--silver { background: #f1f5f9; color: #64748b; }
  &.oad-field-num--bronze { background: #fef0e6; color: #c2692c; }
}

.oad-field-body {
  flex: 1;
  min-width: 0;
}

.oad-field-label {
  font-size: 12px;
  font-weight: 500;
  color: #1a1d26;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.oad-field-bar {
  height: 5px;
  background: #f1f3f5;
  border-radius: 99px;
  overflow: hidden;
}

.oad-field-fill {
  height: 100%;
  background: linear-gradient(90deg, #556ee6, #7c8ff5);
  border-radius: 99px;
  transition: width 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

.oad-field-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  min-width: 50px;
}

.oad-field-count {
  font-size: 12px;
  font-weight: 700;
  color: #1a1d26;
}

.oad-field-pct {
  font-size: 10px;
  color: #8b90a5;
  font-weight: 500;
}

/* Recentes */
.oad-recent-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.oad-recent-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
}

.oad-recent-icon {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 14px;
  flex-shrink: 0;
}

.oad-recent-in .oad-recent-icon {
  background: rgba(52, 195, 143, 0.12);
  color: #34c38f;
}

.oad-recent-out .oad-recent-icon {
  background: rgba(241, 180, 76, 0.12);
  color: #f1b44c;
}

.oad-recent-body {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.oad-recent-name {
  font-size: 12px;
  font-weight: 600;
  color: #1a1d26;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.oad-recent-tag {
  font-size: 11px;
  color: #8b90a5;
}

.oad-recent-time {
  font-size: 11px;
  color: #8b90a5;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}

/* Modal picker (overlay sobre o drawer) */
.oad-picker-overlay {
  position: absolute;
  inset: 0;
  background: rgba(26, 29, 38, 0.4);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.oad-picker {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 12px 60px rgba(26, 29, 38, 0.22);
  width: 100%;
  max-width: 480px;
  max-height: 90%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.oad-picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid #f1f3f5;
  background: #fafbfc;
}

.oad-picker-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.oad-picker-header-icon {
  font-size: 16px;
  color: #556ee6;
}

.oad-picker-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1d26;
}

.oad-picker-close {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #f5f6fa;
  border: none;
  font-size: 18px;
  color: #8b90a5;
  cursor: pointer;
  display: grid;
  place-items: center;

  &:hover { background: #eaecf2; }
}

.oad-picker-body {
  overflow-y: auto;
  padding: 14px 18px;
}

.oad-picker-group {
  margin-bottom: 16px;

  &:last-child { margin-bottom: 0; }
}

.oad-picker-group-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #8b90a5;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #f1f3f5;
}

.oad-picker-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.oad-picker-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  border: 1.5px solid #eaecf2;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
  color: #4a5068;
  text-align: left;

  &:hover {
    background: #f5f6ff;
    border-color: #c5caf5;
  }

  &.selected {
    background: rgba(85, 110, 230, 0.07);
    border-color: rgba(85, 110, 230, 0.35);
    color: #3a4fa3;
  }

  i { font-size: 14px; color: #556ee6; }

  span {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.oad-picker-footer {
  padding: 10px 18px;
  border-top: 1px solid #f1f3f5;
}

.oad-picker-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 7px 10px;
  border: 1.5px dashed #e0b4b4;
  border-radius: 5px;
  background: #fff8f8;
  color: #c0392b;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: #fef0f0;
    border-color: #c0392b;
  }
}

@media (max-width: 640px) {
  .oad-drawer { width: 100vw; }
  .oad-kpi-grid { grid-template-columns: 1fr; }
}
</style>
